<template>
  <div class="voyage-picker">
    <span class="voyage-picker-side" :style="{ background: sideColor }">{{ side }}</span>
    <b-form-select
      :value="massId"
      :options="assetOptions"
      value-field="massId"
      text-field="massName"
      size="sm"
      class="voyage-picker-select"
      @change="$emit('asset-change', $event)"
    >
      <template #first>
        <b-form-select-option :value="null" disabled>Select asset</b-form-select-option>
      </template>
    </b-form-select>
    <b-form-select
      :value="voyageId"
      :options="voyageOptions"
      :disabled="!massId || loading"
      size="sm"
      class="voyage-picker-select"
      @change="$emit('voyage-change', $event)"
    >
      <template #first>
        <b-form-select-option :value="null" disabled>
          {{ loading ? 'Loading voyages...' : 'Select voyage' }}
        </b-form-select-option>
      </template>
    </b-form-select>
    <small v-if="error" class="voyage-picker-error">{{ error }}</small>
  </div>
</template>

<script>
import axios from 'axios';
import VoyageMasterSvc from '@/services/VoyageMasterSvc';
import { fromApiDatetime } from '@/util/apiDatetime';
import { SIDE_COLORS } from '@/views/voyage/utils/voyageCompareSeries';

const voyageMaster = new VoyageMasterSvc();

export default {
  name: 'VoyagePicker',
  props: {
    side: {
      type: String,
      required: true,
    },
    otherSide: {
      type: String,
      required: true,
    },
    assets: {
      type: Array,
      default: () => [],
    },
    massId: {
      type: [Number, String],
      default: null,
    },
    voyageId: {
      type: Number,
      default: null,
    },
    otherVoyageId: {
      type: Number,
      default: null,
    },
    // master voyage terpilih, buat jaga-jaga gak ada di list 100 teratas
    currentVoyage: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      voyages: [],
      loading: false,
      error: '',
    };
  },
  computed: {
    sideColor() {
      return SIDE_COLORS[this.side.toLowerCase()];
    },
    // asset voyage terpilih bisa gak ada di daftar asset user
    assetOptions() {
      const current = this.currentVoyage;
      const missing = current && current.mvygMass
        && !this.assets.some((asset) => String(asset.massId) === String(current.mvygMassId));
      if (!missing) return this.assets;
      return [{ massId: current.mvygMassId, massName: current.mvygMass.massName }, ...this.assets];
    },
    voyageOptions() {
      const list = [...this.voyages];
      const current = this.currentVoyage;
      if (current && String(current.mvygMassId) === String(this.massId)
        && !list.some((voyage) => voyage.mvygId === current.mvygId)) {
        list.unshift(current);
      }
      return list.map((voyage) => {
        const takenByOther = voyage.mvygId === this.otherVoyageId;
        return {
          value: voyage.mvygId,
          text: takenByOther
            ? `${this.optionText(voyage)} (selected in ${this.otherSide})`
            : this.optionText(voyage),
          disabled: takenByOther,
        };
      });
    },
  },
  watch: {
    massId() {
      this.loadVoyages();
    },
  },
  // bukan watch immediate: itu jalan sebelum created, token belum ada
  created() {
    this.requestToken = 0;
    this.loadVoyages();
  },
  methods: {
    // Nama · No. · 01 Aug → 13 Aug 2026
    optionText(voyage) {
      const start = fromApiDatetime(voyage.mvygStartTime);
      const end = fromApiDatetime(voyage.mvygEndTime);
      const period = start.isValid() && end.isValid()
        ? `${start.format('DD MMM')} → ${end.format('DD MMM YYYY')}`
        : '';
      return [voyage.mvygName, voyage.mvygNumber, period].filter(Boolean).join(' · ');
    },
    async loadVoyages() {
      this.requestToken += 1;
      const token = this.requestToken;
      this.voyages = [];
      this.error = '';
      if (!this.massId) {
        this.loading = false;
        return;
      }
      this.loading = true;
      try {
        const response = await voyageMaster.getAll({
          page: 1,
          max: 100,
          'orders[mvygStartTime]': 'desc',
          'finds[mvygMassId]': this.massId,
        });
        if (token !== this.requestToken) return;
        // error datang dengan HTTP 200
        if (response.status?.error) {
          this.error = 'Failed to load voyages.';
          return;
        }
        this.voyages = response.data || [];
      } catch (error) {
        if (token !== this.requestToken || axios.isCancel(error)) return;
        console.error('VoyagePicker voyages error:', error?.message);
        this.error = 'Failed to load voyages.';
      } finally {
        if (token === this.requestToken) this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.voyage-picker {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) minmax(0, 1.6fr);
  gap: 6px;
  align-items: center;
  min-width: 0;
}

.voyage-picker-side {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  color: #fff;
  font-size: 12.25px;
  font-weight: 600;
}

.voyage-picker-select {
  min-width: 0;
  font-size: 14px;
}

.voyage-picker-error {
  grid-column: 1 / -1;
  font-size: 12.25px;
  color: #dc3545;
}

@media (max-width: 575.98px) {
  .voyage-picker {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .voyage-picker-select {
    grid-column: 2;
  }
}
</style>
